import { useRef } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { useRefresh } from "lib/@hooks/useRefresh"

import { Frag } from "lib/@components/slot/frag"
import { pick } from "lib/pick"
import { getKey } from "lib/@components/get-key"
import { Simple } from "lib/@components/simple"

export function Repeat({
    collection = [],
    list = collection,
    children,
    item = children?.type ? children : <Simple />,
    itemFn = () => false,
    pass = "item",
    keyFn = getKey,
    fallback = <Frag />,
    bind,
}) {
    const refresh = useRefresh()
    const objectRemoveClosures = useRef(new WeakMap())
    const primitiveRemoveClosures = useRef(new Map())
    if (typeof keyFn === "string") keyFn = pick(keyFn)

    if (list instanceof Error) {
        console.warn("Attempting to iterate over an error", list)
        return null
    }

    if (!Array.isArray(list) && typeof list === "object" && list) {
        list = Object.entries(list)
    }

    return !list?.length
        ? fallback
        : list?.map((iterated, index, ar) => {
              const key = keyFn(iterated, index)
              if (bind) {
                  return (
                      <Bound key={key} target={iterated}>
                          {itemFn(iterated, index, ar) || (
                              <item.type
                                  remove={getRemoveClosure(iterated)}
                                  {...{
                                      ...item.props,
                                      [pass]: iterated,
                                      previous: ar[index - 1],
                                      index,
                                      last: list.length === index + 1,
                                  }}
                              />
                          )}
                      </Bound>
                  )
              }
              const result = itemFn(iterated, index, ar)
              if (typeof item.type === "string" && !result) {
                  return <item.type key={key} {...item.props} />
              }
              return (
                  result || (
                      <item.type
                          remove={getRemoveClosure(iterated)}
                          key={key}
                          {...{
                              ...item.props,
                              [pass]: iterated,
                              previous: ar[index - 1],
                              next: ar[index + 1],
                              index,
                              last: list.length === index + 1,
                          }}
                      />
                  )
              )
          })

    function remove(item) {
        const idx = list.indexOf(item)
        if (idx >= 0) {
            list.splice(idx, 1)
            refresh()
        }
    }

    function getRemoveClosure(itemRef) {
        if (itemRef && typeof itemRef === "object") {
            const cache = objectRemoveClosures.current
            if (!cache.has(itemRef)) cache.set(itemRef, () => remove(itemRef))
            return cache.get(itemRef)
        }
        const cache = primitiveRemoveClosures.current
        if (!cache.has(itemRef)) cache.set(itemRef, () => remove(itemRef))
        return cache.get(itemRef)
    }
}
